import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE_URL } from '../config/api';
import './AdminComponents.css';

const UserManagement = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [hasMore, setHasMore] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Create user form state
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    role: 'user',
    status: 'active',
    description: ''
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Password reset state
  const [passwordResetResult, setPasswordResetResult] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Load users on component mount and when filters change
  useEffect(() => {
    loadUsers(true);
  }, [statusFilter, roleFilter, sortBy, sortOrder]);

  const getAuthHeaders = () => {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('auth_token='))
      ?.split('=')[1];
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const loadUsers = async (reset = false) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        limit: '50',
        sortBy,
        sortOrder
      });

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (roleFilter !== 'all') {
        params.append('role', roleFilter);
      }
      if (!reset && lastEvaluatedKey) {
        params.append('lastEvaluatedKey', encodeURIComponent(JSON.stringify(lastEvaluatedKey)));
      }

      const response = await fetch(`${API_BASE_URL}/admin/users?${params}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to load users: ${response.statusText}`);
      }

      const data = await response.json();

      if (reset) {
        setUsers(data.users || []);
      } else {
        setUsers(prev => [...prev, ...(data.users || [])]);
      }

      setHasMore(data.hasMore || false);
      setLastEvaluatedKey(data.lastEvaluatedKey || null);

    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!createForm.email || !createForm.password) {
      setCreateError('Email and password are required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(createForm.email)) {
      setCreateError('Please enter a valid email address');
      return;
    }

    // Password strength validation (matching backend requirements)
    const password = createForm.password;
    const passwordErrors = [];

    if (password.length < 8) {
      passwordErrors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      passwordErrors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      passwordErrors.push('Password must contain at least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      passwordErrors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      passwordErrors.push('Password must contain at least one special character');
    }

    if (passwordErrors.length > 0) {
      setCreateError(`Password requirements not met:\n• ${passwordErrors.join('\n• ')}`);
      return;
    }

    try {
      setCreateLoading(true);
      setCreateError(null);

      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(createForm)
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Handle detailed validation errors
        if (errorData.details && errorData.details.password) {
          const passwordErrors = Array.isArray(errorData.details.password)
            ? errorData.details.password
            : [errorData.details.password];
          throw new Error(`Password requirements not met:\n• ${passwordErrors.join('\n• ')}`);
        }

        // Handle other detailed errors
        if (errorData.details) {
          const detailMessages = Object.entries(errorData.details)
            .filter(([key, value]) => value)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
          if (detailMessages) {
            throw new Error(detailMessages);
          }
        }

        throw new Error(errorData.error || errorData.message || `Failed to create user: ${response.statusText}`);
      }

      const data = await response.json();

      // Add new user to the list
      setUsers(prev => [data.user, ...prev]);

      // Reset form and close
      setCreateForm({
        email: '',
        password: '',
        role: 'user',
        status: 'active',
        description: ''
      });
      setShowCreateForm(false);

    } catch (err) {
      console.error('Error creating user:', err);
      setCreateError(err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update user status: ${response.statusText}`);
      }

      const data = await response.json();

      // Update user in the list
      setUsers(prev => prev.map(user =>
        user.userId === userId ? { ...user, status: data.user.status } : user
      ));

    } catch (err) {
      console.error('Error updating user status:', err);
      setError(err.message);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Are you sure you want to reset this user\'s password? A temporary password will be generated.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ generateTemporary: true })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to reset password: ${response.statusText}`);
      }

      const data = await response.json();

      // Store the result and show modal
      setPasswordResetResult({
        success: true,
        temporaryPassword: data.temporaryPassword,
        userEmail: users.find(u => u.userId === userId)?.email || 'Unknown user'
      });
      setShowPasswordModal(true);

    } catch (err) {
      console.error('Error resetting password:', err);
      setPasswordResetResult({
        success: false,
        error: err.message
      });
      setShowPasswordModal(true);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here if desired
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.userId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'status-badge status-active';
      case 'suspended': return 'status-badge status-suspended';
      case 'inactive': return 'status-badge status-inactive';
      default: return 'status-badge';
    }
  };

  const getRoleBadgeClass = (role) => {
    return role === 'admin' ? 'role-badge role-admin' : 'role-badge role-user';
  };

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div>
          <h2>👥 User Management</h2>
          <p>Manage user accounts, roles, and permissions</p>
        </div>
        <button
          className="create-user-button"
          onClick={() => setShowCreateForm(true)}
        >
          ➕ Create User
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Create New User</h3>
              <button
                className="modal-close"
                onClick={() => setShowCreateForm(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="create-user-form">
              {createError && (
                <div className="form-error">
                  ⚠️ {createError}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                  placeholder="user@example.com"
                  autoComplete="email"
                  inputMode="email"
                  spellCheck="false"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                  required
                  placeholder="Enter secure password"
                  minLength="8"
                  autoComplete="new-password"
                />
                <div className="password-requirements">
                  <small>Password must contain:</small>
                  <ul>
                    <li>At least 8 characters</li>
                    <li>One uppercase letter (A-Z)</li>
                    <li>One lowercase letter (a-z)</li>
                    <li>One number (0-9)</li>
                    <li>One special character (!@#$%^&*)</li>
                  </ul>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Admin Description</label>
                <textarea
                  id="description"
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description visible only to admins..."
                  rows="3"
                  maxLength="500"
                />
                <small className="form-note">
                  This description is only visible to administrators and can be used for internal notes about the user.
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="role">Role</label>
                  <select
                    id="role"
                    value={createForm.role}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    value={createForm.status}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={createLoading}
                >
                  {createLoading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Result Modal */}
      {showPasswordModal && passwordResetResult && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {passwordResetResult.success ? '🔑 Password Reset Successful' : '⚠️ Password Reset Failed'}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordResetResult(null);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {passwordResetResult.success ? (
                <div className="password-reset-success">
                  <p><strong>User:</strong> {passwordResetResult.userEmail}</p>
                  <p><strong>Temporary Password:</strong></p>
                  <div className="password-display">
                    <code className="temp-password">{passwordResetResult.temporaryPassword}</code>
                    <button
                      className="copy-button"
                      onClick={() => copyToClipboard(passwordResetResult.temporaryPassword)}
                      title="Copy to clipboard"
                    >
                      📋 Copy
                    </button>
                  </div>
                  <div className="password-instructions">
                    <p><strong>Important:</strong></p>
                    <ul>
                      <li>Please provide this temporary password to the user securely</li>
                      <li>Ask the user to change their password on first login</li>
                      <li>This password meets all security requirements</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="password-reset-error">
                  <p>Failed to reset password: {passwordResetResult.error}</p>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button
                className="submit-button"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordResetResult(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="user-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search users by email or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-controls">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="user">Users</option>
            <option value="admin">Admins</option>
          </select>

          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order);
            }}
          >
            <option value="createdAt-desc">Newest First</option>
            <option value="createdAt-asc">Oldest First</option>
            <option value="email-asc">Email A-Z</option>
            <option value="email-desc">Email Z-A</option>
            <option value="lastLoginAt-desc">Last Login</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="users-table-container">
        {loading && users.length === 0 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.userId}>
                  <td>
                    <div className="user-info">
                      <div className="user-email">{user.email}</div>
                      <div className="user-id">{user.userId}</div>
                    </div>
                  </td>
                  <td>
                    <span className={getRoleBadgeClass(user.role)}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <select
                      value={user.status}
                      onChange={(e) => handleStatusChange(user.userId, e.target.value)}
                      className={getStatusBadgeClass(user.status)}
                      disabled={user.userId === currentUser?.userId}
                    >
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </td>
                  <td>
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td>
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td>
                    <div className="user-actions">
                      <Link
                        to={`/admin/users/${user.userId}`}
                        className="action-button view-button"
                        title="View Details"
                      >
                        👁️
                      </Link>
                      <button
                        onClick={() => handleResetPassword(user.userId)}
                        className="action-button reset-button"
                        title="Reset Password"
                        disabled={user.userId === currentUser?.userId}
                      >
                        🔑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {filteredUsers.length === 0 && !loading && (
          <div className="empty-state">
            <p>No users found matching your criteria.</p>
          </div>
        )}

        {hasMore && (
          <div className="load-more-container">
            <button
              onClick={() => loadUsers(false)}
              disabled={loading}
              className="load-more-button"
            >
              {loading ? 'Loading...' : 'Load More Users'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;